"use client";

import { useEffect, useRef, type RefObject } from "react";

// 2026-09-06 lote 2.1 — patrón diálogo completo: foco inicial + trap Tab +
// Escape + retorno al trigger. Un solo hook para los 5 diálogos custom del
// chat (preview documento, Modo Examen, 2 lightboxes, imagen de sidebar) en
// vez de 5 implementaciones divergentes. Los primitivos Radix ya traen su
// propio trap; esto es solo para overlays `role="dialog"` hechos a mano.
const FOCUSABLE_SELECTOR =
  'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

// Pila de diálogos abiertos: con lightbox (z-100) + modal de edición (z-110)
// apilados, solo el superior responde a Escape/Tab.
const openStack: Array<HTMLElement> = [];

function focusablesOf(root: HTMLElement): HTMLElement[] {
  return Array.from(root.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)).filter(
    (el) => !el.hasAttribute("disabled") && el.getAttribute("tabindex") !== "-1"
  );
}

export function useFocusTrapDialog<T extends HTMLElement = HTMLDivElement>(
  open: boolean,
  onClose: () => void
): RefObject<T | null> {
  const dialogRef = useRef<T | null>(null);
  const triggerRef = useRef<HTMLElement | null>(null);
  const onCloseRef = useRef(onClose);
  useEffect(() => {
    onCloseRef.current = onClose;
  });

  // Foco inicial al abrir + retorno al trigger al cerrar/desmontar.
  useEffect(() => {
    if (!open) return;
    triggerRef.current =
      document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const root = dialogRef.current;
    if (root) {
      if (!openStack.includes(root)) openStack.push(root);
      if (!root.hasAttribute("tabindex")) root.setAttribute("tabindex", "-1");
      const first = focusablesOf(root)[0];
      (first ?? root).focus({ preventScroll: true });
    }
    return () => {
      if (root) {
        const idx = openStack.indexOf(root);
        if (idx >= 0) openStack.splice(idx, 1);
      }
      const trigger = triggerRef.current;
      triggerRef.current = null;
      if (trigger && document.contains(trigger)) trigger.focus({ preventScroll: true });
    };
  }, [open]);

  // Escape + trap Tab (capture: los onKeyDown internos no lo rompen).
  useEffect(() => {
    if (!open) return;
    const onKeyDown = (event: KeyboardEvent) => {
      const root = dialogRef.current;
      if (!root || openStack[openStack.length - 1] !== root) return;
      if (event.key === "Escape") {
        event.stopPropagation();
        onCloseRef.current();
        return;
      }
      if (event.key !== "Tab") return;
      const items = focusablesOf(root);
      if (items.length === 0) {
        event.preventDefault();
        return;
      }
      const first = items[0] as HTMLElement;
      const last = items[items.length - 1] as HTMLElement;
      const active = document.activeElement as HTMLElement | null;
      if (event.shiftKey && (active === first || !root.contains(active))) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && (active === last || !root.contains(active))) {
        event.preventDefault();
        first.focus();
      }
    };
    document.addEventListener("keydown", onKeyDown, true);
    return () => document.removeEventListener("keydown", onKeyDown, true);
  }, [open]);

  return dialogRef;
}
